import { ModelTopologyFeatureBinding } from './ModelTopologyFeatureBinding';

/**
 * 收集部分边线参数中的参考点。
 *
 * @param partialEdges 部分边线参数集合。
 * @returns 参考点集合。
 */
function collectPartialEdgeReferencePoints(partialEdges: PartialEdgeParam[] | undefined): BlendPointRef[] {
    return (partialEdges ?? []).flatMap((partialEdge) => [
        partialEdge.startReferencePoint ?? null,
        partialEdge.endReferencePoint ?? null,
    ]).filter((pointRef): pointRef is BlendPointRef => Boolean(pointRef));
}

/**
 * 解析倒角定义中的全部拓扑选择项。
 *
 * @param definition 倒角定义。
 * @returns 去重后的拓扑选择项。
 */
function collectChamferTopologyItems(definition: ChamferFeatureDefinition) {
    const items = [
        ...(definition.items ?? []),
        ...((definition.faceSets ?? []).flatMap((faceSet) => faceSet.items ?? [])),
    ];
    const itemByRef = new Map(items.map((item) => [item.persistentRef, item]));
    return Array.from(itemByRef.values());
}

/**
 * 解析倒角定义中的点类引用。
 *
 * @param definition 倒角定义。
 * @returns 去重后的点类引用。
 */
function collectChamferPointRefs(definition: ChamferFeatureDefinition) {
    const pointRefs = [
        definition.helpPoint ?? null,
        ...collectPartialEdgeReferencePoints(definition.partialEdges),
    ].filter((pointRef): pointRef is BlendPointRef => pointRef !== null);
    const pointByRef = new Map(pointRefs.map((pointRef) => [pointRef.persistentRef ?? JSON.stringify(pointRef.point), pointRef]));
    return Array.from(pointByRef.values());
}

/**
 * 构建倒角模型拓扑绑定。
 *
 * 设计说明：
 * 1. 倒角和圆角一样，真实输入来自模型边、面、顶点以及 help point；
 * 2. binding 层只负责把定义投影成稳定引用，不承担任何 UI 规则判断；
 * 3. 这样 `preview / commit / edit-session` 都能共享同一条 binding 真值链。
 *
 * @param input 构建输入。
 * @returns 可写入命令参数的绑定列表。
 */
export function buildChamferModelTopologyBindings(input: {
    upstreamBodyObjectId?: string | null;
    definition: ChamferFeatureDefinition;
}): SerializedFeatureReferenceBinding[] {
    const items = collectChamferTopologyItems(input.definition);
    const pointRefs = collectChamferPointRefs(input.definition);
    const bodyObjectId = input.upstreamBodyObjectId
        ?? items[0]?.bodyObjectId
        ?? pointRefs[0]?.bodyObjectId
        ?? null;
    if (!bodyObjectId) {
        return [];
    }
    if (items.length <= 0 && pointRefs.length <= 0) {
        return [];
    }
    return [
        ModelTopologyFeatureBinding.createPayload({
            bodyObjectId,
            items,
            pointRefs,
        }),
    ];
}
